import { IMytaverseFile } from '@interfaces/file';
export interface IMapFloor {
  id: string;
  name: string;
  image: IMytaverseFile;
}

export interface ITeleportLocation {
  id: string;
}

export interface ITeleportLocationPosition {
  x: number;
  y: number;
  z: number;
}

export interface ITeleportLocationRotation {
  yaw?: number;
  pitch?: number;
  roll?: number;
}

export interface ITeleportPreview {
  teleportId: string;
  previewImage: IMytaverseFile | null;
  id?: string;
  teleportLocation?: ITeleportLocation;
  name?: string;
  description?: string | null;
  screenshotName?: string;
  positionInWidget?: number;
  showInUi?: boolean;
  position?: ITeleportLocationPosition;
  rotation?: ITeleportLocationRotation;
}

export type TrainingTaskType = 'procedural' | 'evaluative';

export interface ITrainingTask {
  taskId: string;
  name: string;
  expectedDurationSec: number;
  // When absent, the task is treated as 'procedural' (see trainingAnalytics).
  type?: TrainingTaskType;
}

export interface ITrainingModule {
  moduleId: string;
  name: string;
  tasks: ITrainingTask[];
}

export interface IMapLayoutElementGeometry {
  kind: 'level' | 'class' | 'greybox';
  asset?: string;
  package?: string;
  available: boolean;
  cooked?: boolean;
}

// The brandable spots a build found inside the element's room level. Computed at build
// time, so it is absent whenever the level could not be read — which is not the same
// answer as `total: 0`, and one shipped corridor really does have no spots at all.
export interface IMapLayoutElementSpots {
  total: number;
  // Authored spot types (IMAGE / VIDEO), already normalised the way the runtime does.
  byType?: Record<string, number>;
  // Unique within a module, not within a world: a module placed twice brings its ids twice.
  ids?: string[];
  // Present only when the module's own ids are already broken — an authoring bug, where
  // the second spot silently shows the first one's media.
  duplicateIds?: string[];
  unnamed?: number;
}

export interface IMapLayoutElement {
  id: string;
  displayName: string;
  category: string;
  // Authored prose about what this element is, shown where the picker has room for a sentence —
  // the wall-layout step. Optional and purely descriptive: the build copies authored catalog
  // fields verbatim, so supplying it needs no UE or backend change, and its absence falls back to
  // the spot counts the build measured.
  description?: string;
  thumbnailUrl?: string;
  // Superseded by the family/direction pair below. Still read for catalogs shipped by a
  // build that predates the split — and its left/right were inverted, so it is mapped by
  // the turn it produced rather than by the word in it (see mapEditorCatalog).
  type?: string;
  // Kept plain strings rather than unions: a newer build may ship a family or direction
  // this hub version has never heard of, and the picker groups on whatever it finds
  // rather than dropping what it cannot name.
  family?: string;
  direction?: string;
  size?: string;
  // 'module' (default) or 'end' — a cap that seals the chain. UE drops every room
  // requested after one, so caps are never offered as pickable modules.
  role?: string;
  geometryLevel?: string;
  geometryClass?: string;
  overrideExit?: boolean;
  exitOffset?: number[];
  exitYawDeg?: number;
  geometry?: IMapLayoutElementGeometry;
  spots?: IMapLayoutElementSpots;
}

export interface IMapLayoutCatalog {
  version: number;
  elements: IMapLayoutElement[];
  // Element id UE closes an open chain with. Emitted only when it names an element the
  // build actually ships; absent means this build closes no chains.
  defaultEndElement?: string;
}

export interface IMapLayout {
  version: number;
  rooms: string[];
}

export const MAP_LAYOUT_VERSION = 1;

export interface IMapRegion {
  name: string;
  type?: string;
  spots?: string[];
  // Marks the region UE designates as the map's entry point. When present,
  // this real region is used as the "on join" AI trigger instead of the
  // synthetic 'landing' pseudo-trigger.
  isStartingPoint?: boolean;
}

export interface IMapFeatureFlag {
  slug: string;
  name?: string;
  // Absent means on, so a flag added to a map applies until an experience opts
  // out; a flag that ships off must be enabled per experience.
  enabledByDefault?: boolean;
}

export interface IBrandingProperty {
  propertyId: string;
  displayName: string;
  // Kept a plain string rather than a union: a newer build may ship a type this
  // hub version has no widget for, and it must survive the round trip.
  type: string;
  // Absent means "no default" — an empty string is a default of empty.
  default?: string;
  authoredAssetPath?: string;
  // Absent means an open field; present means a closed set.
  allowedValues?: string[];
}

// The catalogue entry: what a build declares as brandable. UE calls this
// `brandingId` in the build metadata — renamed here because `brandingId` is the
// id of an IBranding record.
export interface IBrandingEntity {
  entityId: string;
  displayName: string;
  entityType: string;
  maps: string[];
  properties: IBrandingProperty[];
}

export interface IMap {
  mapId: string;
  ueIdentifier: string;
  name: string;

  displayName: string;
  mapName: string;
  imageBaseMap: string;
  id: string;
  thumbnail: string;

  floorPlans?: IMapFloor[];
  teleportPreviews?: ITeleportPreview[];
  trainingModules?: ITrainingModule[];
  regions?: IMapRegion[];
  layoutCatalog?: IMapLayoutCatalog;
  mapFeatureFlags?: IMapFeatureFlag[];
  brandingEntities?: IBrandingEntity[];
}
